import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { PlusCircle } from 'lucide-react';
import { getSubjectName } from '../../../utils/quizUtils';
import { SUBJECTS } from '../../../constants';

const QuizAssignmentForm = ({
  isTeacher,
  selectedStudent,
  isAssigning,
  selectedContentId,
  contents,
  onStartAssigning,
  onCancelAssigning,
  onContentSelect,
  onAssignQuiz
}) => {
  // 検索とフィルタの状態
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubjectFilter, setSelectedSubjectFilter] = useState('all');
  // ソート機能の状態を追加
  const [sortOption, setSortOption] = useState('created-desc');

  // フィルタリングされたコンテンツを取得
  const filteredContents = useMemo(() => {
    let filtered = contents;

    // 教科フィルタ
    if (selectedSubjectFilter !== 'all') {
      filtered = filtered.filter(content => content.subject === selectedSubjectFilter);
    }

    // 検索フィルタ
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(content => 
        content.title.toLowerCase().includes(searchLower)
      );
    }

    // ソート処理を追加
    const sorted = [...filtered].sort((a, b) => {
      switch (sortOption) {
        case 'title-asc':
          return a.title.localeCompare(b.title, 'ja');
        case 'title-desc':
          return b.title.localeCompare(a.title, 'ja');
        case 'quiz-count-asc':
          return (Object.keys(a.quizzes || {}).length) - (Object.keys(b.quizzes || {}).length);
        case 'quiz-count-desc':
          return (Object.keys(b.quizzes || {}).length) - (Object.keys(a.quizzes || {}).length);
        case 'created-asc':
          return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
        case 'created-desc':
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        default:
          return 0;
      }
    });

    return sorted;
  }, [contents, selectedSubjectFilter, searchTerm, sortOption]);

  // 選択されたコンテンツがフィルタ結果に含まれているかチェック
  const isSelectedContentVisible = selectedContentId && 
    filteredContents.some(content => content.id === selectedContentId);

  // フィルタ変更時に選択をクリアするかどうかをチェック
  React.useEffect(() => {
    if (selectedContentId && !isSelectedContentVisible) {
      onContentSelect('');
    }
  }, [selectedContentId, isSelectedContentVisible, onContentSelect]);

  // キャンセル時の状態リセット関数
  const handleCancel = () => {
    setSearchTerm('');
    setSelectedSubjectFilter('all');
    setSortOption('created-desc');
    onCancelAssigning();
  };

  // 問題数を計算するヘルパー関数
  const getQuizCount = (content) => {
    if (!content.quizzes) return 0;
    return Object.keys(content.quizzes).length;
  };

  // 制限時間情報を取得するヘルパー関数
  const getTimeLimitInfo = (content) => {
    if (content.freeNavigation && content.timeLimitPerContent > 0) {
      const minutes = Math.floor(content.timeLimitPerContent / 60);
      const seconds = content.timeLimitPerContent % 60;
      return `全体${minutes > 0 ? `${minutes}分` : ''}${seconds > 0 ? `${seconds}秒` : ''}`;
    } else if (!content.freeNavigation && content.timeLimitPerQuestion > 0) {
      const minutes = Math.floor(content.timeLimitPerQuestion / 60);
      const seconds = content.timeLimitPerQuestion % 60;
      return `1問${minutes > 0 ? `${minutes}分` : ''}${seconds > 0 ? `${seconds}秒` : ''}`;
    }
    return 'なし';
  };

  // 教師で生徒が選択されている場合のみ表示
  if (!isTeacher || !selectedStudent) {
    return null;
  }

  return (
    <div className="mb-6">
      {!isAssigning ? (
        <button
          onClick={() => {
            onStartAssigning();
          }}
          className="w-full py-4 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 flex items-center justify-center gap-2"
          aria-label="クイズを割り当てるボタン"
        >
          <PlusCircle size={20} />
          クイズを割り当て
        </button>
      ) : (
        <div className="mt-6 p-4 bg-yellow-50 rounded shadow">
          <h3 className="text-xl font-semibold text-purple-700 mb-4">クイズを割り当てる</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1" htmlFor="content-select">
                コンテンツを選択
              </label>
              
              {/* 検索とフィルタのコントロール */}
              <div className="space-y-2 mb-3">
                <div className="flex gap-2">
                  {/* 教科フィルタ */}
                  <select
                    value={selectedSubjectFilter}
                    onChange={(e) => {
                      setSelectedSubjectFilter(e.target.value);
                    }}
                    className="flex-1 p-2 border rounded text-sm"
                    aria-label="教科フィルタ"
                  >
                    <option value="all">全教科</option>
                    {SUBJECTS.map((subject) => (
                      <option key={subject.id} value={subject.id}>
                        {subject.name}
                      </option>
                    ))}
                  </select>
                  
                  {/* 検索ボックス */}
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      placeholder="タイトルで検索..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                      }}
                      className="w-full p-2 border rounded text-sm pr-8"
                      aria-label="コンテンツ検索"
                    />
                    {searchTerm && (
                      <button
                        onClick={() => {
                          setSearchTerm('');
                        }}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
                        aria-label="検索をクリア"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
                
                {/* ソート機能を追加 */}
                <div className="flex gap-2">
                  <select
                    value={sortOption}
                    onChange={(e) => {
                      setSortOption(e.target.value);
                    }}
                    className="flex-1 p-2 border rounded text-sm"
                    aria-label="ソート順"
                  >
                    <option value="created-desc">作成日時 (新→古)</option>
                    <option value="created-asc">作成日時 (古→新)</option>
                    <option value="title-asc">タイトル (あ→ん)</option>
                    <option value="title-desc">タイトル (ん→あ)</option>
                    <option value="quiz-count-asc">問題数 (少→多)</option>
                    <option value="quiz-count-desc">問題数 (多→少)</option>
                  </select>
                </div>
                
                {/* 結果件数表示 */}
                {(searchTerm || selectedSubjectFilter !== 'all') && (
                  <div className="text-xs text-gray-600">
                    {filteredContents.length}件のコンテンツが見つかりました
                    {searchTerm && ` (検索: "${searchTerm}")`}
                    {selectedSubjectFilter !== 'all' && ` (教科: ${SUBJECTS.find(s => s.id === selectedSubjectFilter)?.name})`}
                  </div>
                )}
              </div>

              {/* コンテンツリスト表示 */}
              <div className="border rounded max-h-60 overflow-y-auto">
                {filteredContents.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    {searchTerm || selectedSubjectFilter !== 'all' 
                      ? '条件に合うコンテンツが見つかりません'
                      : 'コンテンツがありません'}
                  </div>
                ) : (
                  <div className="space-y-1 p-2">
                    {filteredContents.map((content) => (
                      <div
                        key={content.id}
                        onClick={() => {
                          onContentSelect(content.id);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onContentSelect(content.id);
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        className={`p-3 rounded cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-purple-300 ${
                          selectedContentId === content.id
                            ? 'bg-purple-100 border-2 border-purple-300 shadow-sm'
                            : 'bg-gray-50 hover:bg-gray-100 border border-gray-200 hover:border-gray-300'
                        }`}
                        aria-pressed={selectedContentId === content.id}
                        aria-label={`コンテンツを選択: ${content.title}`}
                      >
                        <div className="font-medium text-sm text-purple-700 flex items-center">
                          📚 {content.title}
                          {selectedContentId === content.id && (
                            <span className="ml-2 text-purple-500">✓</span>
                          )}
                        </div>
                        <div className="text-xs text-gray-600 mt-1 space-y-0.5">
                          <div>教科: {getSubjectName(content.subject)}</div>
                          <div>問題数: {getQuizCount(content)}問</div>
                          <div>制限時間: {getTimeLimitInfo(content)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (onAssignQuiz) {
                    onAssignQuiz();
                  }
                }}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                disabled={!selectedContentId}
                aria-label="クイズを割り当てるボタン"
              >
                割り当て
              </button>
              <button
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                aria-label="キャンセルボタン"
              >
                キャンセル
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

QuizAssignmentForm.propTypes = {
  isTeacher: PropTypes.bool.isRequired,
  selectedStudent: PropTypes.object,
  isAssigning: PropTypes.bool.isRequired,
  selectedContentId: PropTypes.string.isRequired,
  contents: PropTypes.array.isRequired,
  onStartAssigning: PropTypes.func.isRequired,
  onCancelAssigning: PropTypes.func.isRequired,
  onContentSelect: PropTypes.func.isRequired,
  onAssignQuiz: PropTypes.func.isRequired
};

export default QuizAssignmentForm; 